/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Adding one seat, answered in place.
 *
 * The button used to dump the operator on the whole subscription tab; the
 * only question it raises is "what does one more seat cost today", so this
 * modal answers exactly that: the tier (preselected from the block the
 * click came from), the pro-rata charge to the renewal, and what the
 * subscription bills from then on — reading the cycle (monthly or yearly)
 * from the subscription itself.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button, Text, UnstyledButton } from '@/shared/ui';
import { Modal } from '@/shared/ui/components/basic/Modal';
import { AgenciesService } from '@/services/requests/housien-services/AgenciesService';
import type { TeamSeatBlock } from '@/services/requests/housien-services/AgenciesService.team';
import type { PlanFeatureType } from '@/features/pricing';
import { useSubscriptionDetail } from '@/features/subscription/hooks/useSubscriptionDetail';
import type { ChangeItemsPreview } from '@/services/requests/housien-services/AgenciesService.team';
import { refusalOf } from '@/features/subscription/hooks/previewRefusal';
import { AddSeatPaymentPending, AddSeatTrialQuote } from './AddSeatTrialQuote';
import {
  asItems, draftFrom,
} from '@/features/subscription/components/seats/seat-draft';

/**
 * AddSeatModal component.
 */
export function AddSeatModal({
  agencyId, initialPlanId, blocks, planTitle, onClose, onAdded,
}: {
  agencyId: string;
  initialPlanId: string;
  blocks: TeamSeatBlock[];
  plans: PlanFeatureType[];
  planTitle: (planId: string) => string | null;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t, i18n } = useTranslation('features/subscription');
  const { data: sub, isLoading } = useSubscriptionDetail(agencyId);
  const [planId, setPlanId] = useState(initialPlanId);
  const [paying, setPaying] = useState(false);

  const money = (cents: number) => new Intl.NumberFormat(i18n.language, {
    style: 'currency', currency: 'BRL',
  }).format(cents / 100);

  const items = sub?.items ?? [];
  const yearly = sub?.billing?.billing_frequency === 'yearly';
  const renewalDate = sub?.next_renewal_date
    ? new Date(sub.next_renewal_date).toLocaleDateString(i18n.language)
    : null;

  const draft = useMemo(() => {
    const next = draftFrom(items);
    next[planId] = (next[planId] ?? 0) + 1;
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, planId]);

  // Asked, not computed. The figure used to be worked out here from a
  // billing cycle this client never received, so an annual subscription was
  // prorated over thirty days and the modal quoted a whole year as if it
  // were a month. Payien prices what Payien collects.
  const [preview, setPreview] = useState<ChangeItemsPreview | null>(null);
  const [quoting, setQuoting] = useState(true);
  const [quoteFailed, setQuoteFailed] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setQuoting(true);
    setQuoteFailed(false);
    setPaymentPending(false);
    setPreview(null);
    AgenciesService.previewSubscriptionItems(agencyId, asItems(draft))
      .then((answer) => { if (live) setPreview(answer); })
      .catch((error) => {
        if (!live) return;
        const refused = refusalOf(error) === 'payment_pending';
        setPaymentPending(refused);
        setQuoteFailed(!refused);
      })
      .finally(() => { if (live) setQuoting(false); });
    return () => { live = false; };
  }, [agencyId, draft, quoteAttempt]);

  const trialing = preview?.next_charge_amount_cents !== undefined;
  const chargedNow = preview?.charged_today_cents ?? 0;
  const renewalTotal = preview?.new_total_cents ?? 0;
  const days = preview?.days_remaining ?? 0;

  const confirm = async () => {
    setPaying(true);
    try {
      await AgenciesService.changeSubscriptionItems(agencyId, asItems(draft));
      toast.success(t('v2.seats.addModal.added'));
      onAdded();
      onClose();
    } catch (error) {
      // The preview can pass and the commit still be refused as pending.
      if (refusalOf(error) === 'payment_pending') setPaymentPending(true);
      else toast.error(t('v2.seats.addModal.failed'));
      setPaying(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title={t('v2.seats.addModal.title')}>
      <div
        data-testid="add-seat-modal"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {blocks.map((block) => {
            const active = block.plan_id === planId;
            return (
              <UnstyledButton
                key={block.plan_id}
                data-testid={`add-seat-tier-${block.plan_id}`}
                onClick={() => setPlanId(block.plan_id)}
                style={{
                  padding: 'var(--space-1-5) var(--space-3)',
                  borderRadius: 'var(--radius-full)',
                  border: active
                    ? '1.5px solid var(--seed-base)'
                    : '1px solid var(--border-subtle)',
                  background: active ? 'var(--brand-beige)' : 'var(--surface-primary)',
                  fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
                  color: 'var(--seed-base)', cursor: 'pointer',
                }}
              >
                {planTitle(block.plan_id) ?? block.plan_id}
              </UnstyledButton>
            );
          })}
        </div>

        {isLoading || !sub || quoting ? (
          <Text style={{ fontSize: 'var(--text-small)', color: 'var(--text-tertiary-soft)' }}>
            {t('v2.seats.addModal.loading')}
          </Text>
        ) : paymentPending ? (
          <AddSeatPaymentPending />
        ) : quoteFailed || !preview ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
            <Text style={{ fontSize: 'var(--text-small)', lineHeight: 1.5, color: 'var(--status-error-text)' }}>
              {t('v2.seats.quoteFailed')}
            </Text>
            <Button variant="ghost" size="sm" onClick={() => setQuoteAttempt((attempt) => attempt + 1)}>
              {t('v2.seats.retryQuote')}
            </Button>
          </div>
        ) : (
          <>
            {trialing ? (
              <AddSeatTrialQuote
                preview={preview}
                planId={planId}
                planTitle={planTitle(planId) ?? planId}
                date={renewalDate}
                money={money}
              />
            ) : (
              <Text
                style={{
                  fontSize: 'var(--text-small)', lineHeight: 1.5,
                  color: 'var(--text-secondary)', textWrap: 'pretty',
                }}
              >
                {renewalDate
                  ? t('v2.seats.addModal.chargedNow', {
                    price: money(chargedNow), days, date: renewalDate,
                  })
                  : t('v2.seats.addModal.chargedNowNoDate', { price: money(chargedNow) })}
                {' '}
                {t(yearly ? 'v2.seats.addModal.afterYearly' : 'v2.seats.addModal.afterMonthly', {
                  total: money(renewalTotal),
                })}
              </Text>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <Button variant="outline" onClick={onClose} disabled={paying}>
                {t('v2.seats.addModal.cancel')}
              </Button>
              <Button
                data-testid="add-seat-confirm"
                onClick={confirm}
                disabled={paying}
                loading={paying}
              >
                {trialing
                  ? t('v2.seats.addModal.trial.confirm', { plan: planTitle(planId) ?? planId })
                  : t('v2.seats.confirmAndPay', { price: money(chargedNow) })}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
